"use client";

import { FormElement, type MsAreaFilterBlock } from "@/payload-types";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { debounce } from "@tanstack/pacer";
import Combobox from "@/components/frontend/ui/form/inputs/combobox";
import { AnyFieldApi } from "@tanstack/react-form-nextjs";
import { Relationship } from "@/lib/utils/theme-configuration-class";
import { useTranslations } from "next-intl";
import { sendMarketSuiteRequest } from "@/lib/marketsuite/client";
import { capitalizeWords } from "@/lib/utils/string";
import { Addresses } from "@/lib/types/marketsuite/datatypes/address";
import { SelectedFilters } from "@/lib/types/marketsuite/datatypes/filters";

interface MsAreaFilterProps extends MsAreaFilterBlock {
	field: AnyFieldApi;
	selectedFilters: SelectedFilters;
	onValueChange?: (value: string[], blockType: "area") => void;
	formVariant?: Relationship<FormElement>;
}

export const MsAreaFilter: React.FC<MsAreaFilterProps> = ({ field, selectedFilters, onValueChange, formVariant, content, labels }) => {
	const t = useTranslations("ms");
	const [areaSearch, setAreaSearch] = useState<string>();
	const { data: addresses } = useQuery({
		queryKey: ["addresses", areaSearch],
		queryFn: async () => {
			if (!areaSearch) return null;

			const params = new URLSearchParams();
			params.set("area", areaSearch);

			const data = await sendMarketSuiteRequest<Addresses>("filters/area", { params, cache: "no-store" });

			if (!data) return null;
			return [...(data.cities ?? []), ...(data.streets ?? []), ...(data.postalCodes ?? [])].map((v) => ({ value: v, label: capitalizeWords(v) }));
		},
	});
	const debouncedSetArea = useMemo(() => debounce((v: string) => setAreaSearch(v), { wait: 600 }), []);

	return (
		<Combobox
			multiple
			options={addresses ?? []}
			id={field.name}
			name={field.name}
			value={(Array.isArray(selectedFilters.area) ? selectedFilters.area : []).map((v: string) => ({ value: v, label: capitalizeWords(v) }))}
			onBlur={field.handleBlur}
			label={content?.label}
			description={content?.description}
			placeholder={content?.placeholder ?? undefined}
			itemClassName="capitalize"
			selectedClassName="capitalize"
			containerClassName=""
			noResultsMessage={areaSearch ? labels?.noResultsMessage || t("filters.area.no-results") : labels?.emptyMessage || t("filters.start-typing")}
			onValueChange={(v) => {
				const value = v.map((v) => v.value);
				field.handleChange(value);
				onValueChange?.(value, "area");
			}}
			onInputValueChange={debouncedSetArea}
			formVariant={formVariant}
		/>
	);
};
